/*jshint esversion: 11 */
import React, { useEffect, useState } from 'react';
import {
  Row,
  Col,
  Button,
  FormGroup,
  Label,
  Modal,
  ModalHeader,
  ModalFooter,
  ModalBody,
  Input,
  Alert
} from 'reactstrap';
import { connect } from 'react-redux';
import Select from 'react-select';
import PageDataLoader from '../Utility/PageDataLoader';
import { AvField, AvForm } from 'availity-reactstrap-validation';

import {
  updateCircular,
  removeCircularFile,
  addCircularFile
} from '../../api/circular';

import { getTagList } from '../../api/tag';
import { getBatchList } from '../../api/batch';

const EditCircularModal = ({
  modalOpenState,
  closeModalHandler,
  circularInfoLoading,
  circularInfo,
  refreshList,
  setSuccessAlert,
  showErrorAlert,
  resetErrorAlert
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [description, setDescription] = useState('');
  const [tagOptions, setTagOptions] = useState([]);
  const [batchOptions, setBatchOptions] = useState([]);

  const [selectedTag, setSelectedTag] = useState(null);
  const [selectedBatches, setSelectedBatches] = useState([]);

  const [existingFiles, setExistingFiles] = useState([]);
  const [newFiles, setNewFiles] = useState([]);

  const buildOptions = (list) =>
    list.map((item) => ({
      value: item._id,
      label: item.name
    }));

  useEffect(() => {
    async function fetchOptions() {
      const tagRes = await getTagList(1, 100);
      if (tagRes) setTagOptions(buildOptions(tagRes?.data || []));

      const batchRes = await getBatchList(1, 100);
      if (batchRes) setBatchOptions(buildOptions(batchRes?.data || []));
    }

    fetchOptions();
  }, []);

  useEffect(() => {
    if (circularInfo) {
      setDescription(circularInfo?.description);
      setSelectedTag({
        value: circularInfo?.tag?._id,
        label: circularInfo?.tag?.name
      });

      setSelectedBatches(
        circularInfo?.batches?.map((b) => ({
          value: b._id,
          label: b.name
        })) || []
      );

      setExistingFiles(circularInfo?.files || []);
      setNewFiles([]);
      setError('');
    }
  }, [circularInfo]);

  const handleRemoveExisting = async (fileId, index) => {
    const res = await removeCircularFile(circularInfo?._id, fileId);

    if (res?.response) {
      setExistingFiles((prev) => prev.filter((_, i) => i !== index));
    } else {
      setError(res?.error || 'Failed to remove file');
    }
  };

  const handleRemoveNew = (index) => {
    setNewFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleFileChange = (e) => {
    setError('');

    const selected = Array.from(e.target.files);

    const invalidFiles = selected.filter(
      (file) =>
        file.type !== 'application/pdf' &&
        !file.name.toLowerCase().endsWith('.pdf')
    );

    if (invalidFiles.length > 0) {
      setError('Only PDF files are allowed');
      e.target.value = null;
      return;
    }

    setNewFiles((prev) => [...prev, ...selected]);

    e.target.value = null;
  };

  const handleSubmit = async () => {
    resetErrorAlert();
    setError('');

    if (!selectedTag) {
      setError('Tag is required');
      return;
    }

    if (!selectedBatches.length) {
      setError('Select at least one batch');
      return;
    }

    setLoading(true);

    try {
      const res = await updateCircular(circularInfo?._id, {
        description,
        tag: selectedTag.value,
        batches: selectedBatches.map((b) => b.value)
      });

      if (!res?.response) {
        setError(res?.error || 'Update failed');
        return;
      }

      if (newFiles.length > 0) {
        const formData = new FormData();

        newFiles.forEach((file) => {
          formData.append('files', file);
        });

        const fileRes = await addCircularFile(circularInfo?._id, formData);

        if (!fileRes?.response) {
          setError(fileRes?.error || 'File upload failed');
          return;
        }
      }

      refreshList && refreshList();

      setSuccessAlert(`Circular Updated successfully`);
      closeModalHandler();
    } catch (e) {
      showErrorAlert(error);
    }

    setLoading(false);
  };

  return (
    <Modal isOpen={modalOpenState} backdrop="static" size="lg">
      <ModalHeader toggle={closeModalHandler}>
        {circularInfoLoading ? 'Loading...' : 'Edit Circular'}
      </ModalHeader>

      <ModalBody>
        {error && <Alert color="danger">{error}</Alert>}

        {circularInfoLoading ? (
          <div className="d-flex justify-content-center">
            <PageDataLoader />
          </div>
        ) : (
          <AvForm>
            <Row>
              <Col md="12">
                <FormGroup>
                  <Label>Description</Label>

                  <AvField
                    name="description"
                    placeholder="Enter Circular Description ..."
                    type="text"
                    className="form-control"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    validate={{
                      required: {
                        value: true,
                        errorMessage: 'Description is required'
                      }
                    }}
                  />
                </FormGroup>
              </Col>
              <Col md="12">
                <FormGroup>
                  <Label>Tag</Label>
                  <Select
                    options={tagOptions}
                    value={selectedTag}
                    onChange={setSelectedTag}
                  />
                </FormGroup>
              </Col>

              <Col md="12">
                <FormGroup>
                  <Label>Batches</Label>
                  <Select
                    options={batchOptions}
                    isMulti
                    value={selectedBatches}
                    onChange={setSelectedBatches}
                  />
                </FormGroup>
              </Col>

              <Col md="12">
                <FormGroup>
                  <Label>Files</Label>

                  {existingFiles.map((f, i) => (
                    <div key={i} className="d-flex mb-2">
                      <Input value={f.originalName || f.documentKey} readOnly />
                      <Button
                        color="danger"
                        size="sm"
                        className="ml-2"
                        onClick={() => handleRemoveExisting(f._id, i)}
                      >
                        Remove
                      </Button>
                    </div>
                  ))}

                  {newFiles.map((f, i) => (
                    <div key={i} className="d-flex mb-2">
                      <Input
                        value={f.name}
                        className="border border-danger rounded"
                        readOnly
                      />
                      <Button
                        color="danger"
                        size="sm"
                        className="ml-2"
                        onClick={() => handleRemoveNew(i)}
                      >
                        Remove
                      </Button>
                    </div>
                  ))}

                  <Input
                    type="file"
                    multiple
                    accept=".pdf,application/pdf"
                    onChange={handleFileChange}
                  />
                </FormGroup>
              </Col>
            </Row>
          </AvForm>
        )}
      </ModalBody>

      <ModalFooter>
        <Button color="primary" onClick={handleSubmit} disabled={loading}>
          {loading ? 'Updating...' : 'Update'}
        </Button>

        <Button color="light" onClick={closeModalHandler}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
};

const mapStateToProps = (state) => ({
  authUser: state.Login?.user
});

export default connect(mapStateToProps)(EditCircularModal);
